import { useSuspenseQuery } from '@apollo/client'
import { PROFILE_INFORMATION_QUERY } from '@gql/Queries'
import {
  ApplicantCode,
  Fc__Customer__NotificationPreferences,
  FcFinSimpleGrowProfileAccountsByMcnQuery,
  Fmcc__Customer__CustomerProfile,
} from '@src/__generated__/graphql'
import { createContext, ReactNode, useContext } from 'react'
import { useAccount } from '@src/AccountContext'

interface CustomerContextProps {
  customerProfile: Fmcc__Customer__CustomerProfile
  notificationPreferences: Omit<Fc__Customer__NotificationPreferences, 'companyBusinessCode'>
  data: FcFinSimpleGrowProfileAccountsByMcnQuery
}

const isApplicantType = (applicantType: string | undefined | null): applicantType is ApplicantCode => {
  if (!applicantType) {
    return false
  }

  return Object.values(ApplicantCode).includes(applicantType as ApplicantCode)
}

export const CustomerContext = createContext<CustomerContextProps | undefined>(undefined)

export const CustomerProvider = ({ children }: { children: ReactNode }) => {
  const { selectedAccount } = useAccount()

  if (selectedAccount.accountNumber === undefined || selectedAccount.accountNumber === null) {
    throw new Error('No account number found')
  }

  if (!isApplicantType(selectedAccount.applicantType)) {
    throw new Error('Invalid account applicant type')
  }

  const { data } = useSuspenseQuery(PROFILE_INFORMATION_QUERY, {
    variables: {
      accountNumber: selectedAccount.accountNumber,
      applicantCode: selectedAccount.applicantType,
    },
  })

  const customerProfile: Fmcc__Customer__CustomerProfile | undefined | null =
    data.fmcc?.customer?.customerAccountRelation?.customerProfile

  if (customerProfile === undefined || customerProfile === null) {
    throw new Error('No customer profile found')
  }

  const notificationPreferences: Omit<Fc__Customer__NotificationPreferences, 'companyBusinessCode'> | undefined | null =
    data.fmcc?.customer?.customerAccountRelation?.notificationPreferences

  if (notificationPreferences === undefined || notificationPreferences === null) {
    throw new Error('No notification preferences found')
  }

  return (
    <CustomerContext.Provider value={{ customerProfile, notificationPreferences, data }}>
      {children}
    </CustomerContext.Provider>
  )
}

export const useCustomer = () => {
  const context = useContext(CustomerContext)
  if (context === undefined) {
    throw new Error('useCustomer must be used within an CustomerProvider')
  }
  return context
}
